// Paket kimliği penceresi (DR5 builder kararı: KpiModal) — Ad (zorunlu), Kod (`pkt_` + slug,
// yalnız oluştururken düzenlenir), Sektör (SearchSelect, kanonik sözlük + "— Sektörsüz —"),
// Çerçeve (opsiyonel, set editörünün `listKpiFrameworks` listesi), Açıklama. Oluştur →
// `createKpiPackage`, düzenle → `updateKpiPackage`; sunucu hatası `packageErrorText` ile.

import { useEffect, useId, useMemo, useState } from "react";
import {
  KPI_PACKAGE_CODE_PREFIX,
  SECTOR_CODES,
  SECTOR_LABELS,
  slugifyKpiCode,
  type KpiFrameworkOption,
  type KpiPackage,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import { createKpiPackage, listKpiFrameworks, updateKpiPackage } from "../../api/kpi";
import { KPI_PACKAGES_COPY, packageErrorText } from "../../lib/kpi-packages-copy";
import { SearchSelect } from "../common/SearchSelect";
import { KpiModal } from "./KpiModal";
import {
  errorBox,
  fieldStyle,
  footerBtn,
  helpText,
  inputStyle,
  labelStyle,
  monoStyle,
} from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY.identity;

export interface PackageIdentityModalProps {
  open: boolean;
  /** null → yeni paket */
  pkg: KpiPackage | null;
  onClose: () => void;
  onSaved: (pkg: KpiPackage) => void;
}

/** Ad → otomatik kod önerisi (`pkt_` öneki + Türkçe katlamalı slug). */
export function suggestPackageCode(name: string): string {
  const slug = slugifyKpiCode(name.trim() === "" ? "paket" : name);
  return slug.startsWith(KPI_PACKAGE_CODE_PREFIX) ? slug : `${KPI_PACKAGE_CODE_PREFIX}${slug}`;
}

const SECTOR_OPTIONS = SECTOR_CODES.map((c) => ({ value: c, label: SECTOR_LABELS[c] }));

export function PackageIdentityModal(p: PackageIdentityModalProps): JSX.Element | null {
  const { open, pkg } = p;
  const ids = { name: useId(), code: useId(), sector: useId(), fw: useId(), desc: useId() };
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [sector, setSector] = useState("");
  const [frameworkId, setFrameworkId] = useState("");
  const [description, setDescription] = useState("");
  const [frameworks, setFrameworks] = useState<KpiFrameworkOption[] | null>(null);
  const [fwFailed, setFwFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triedSubmit, setTriedSubmit] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(pkg?.name ?? "");
    setCode(pkg?.code ?? "");
    setCodeTouched(pkg !== null);
    setSector(pkg?.sectorCode ?? "");
    setFrameworkId(pkg?.frameworkId ?? "");
    setDescription(pkg?.description ?? "");
    setError(null);
    setTriedSubmit(false);
    let alive = true;
    listKpiFrameworks()
      .then((list) => {
        if (alive) setFrameworks(list);
      })
      .catch(() => {
        if (alive) {
          setFrameworks([]);
          setFwFailed(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [open, pkg]);

  const fwOptions = useMemo(
    () => (frameworks ?? []).map((f) => ({ value: f.id, label: f.name })),
    [frameworks],
  );

  if (!open) return null;
  const creating = pkg === null;
  const shownCode = creating && !codeTouched ? suggestPackageCode(name) : code;
  const nameMissing = name.trim() === "";
  const dirty = creating
    ? name.trim() !== "" || description.trim() !== ""
    : name !== pkg.name ||
      (sector || null) !== pkg.sectorCode ||
      (frameworkId || null) !== pkg.frameworkId ||
      (description.trim() || null) !== (pkg.description ?? null);

  async function submit(): Promise<void> {
    setTriedSubmit(true);
    if (nameMissing || busy) return;
    setBusy(true);
    setError(null);
    const common = {
      name: name.trim(),
      description: description.trim() === "" ? null : description.trim(),
      sectorCode: sector === "" ? null : (sector as (typeof SECTOR_CODES)[number]),
      frameworkId: frameworkId === "" ? null : frameworkId,
    };
    try {
      const saved = creating
        ? await createKpiPackage({ ...common, code: shownCode.trim() })
        : await updateKpiPackage(pkg.id, common);
      p.onSaved(saved);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? packageErrorText(e.code, e.message, e.details)
          : packageErrorText("", e instanceof Error ? e.message : undefined),
      );
    } finally {
      setBusy(false);
    }
  }

  const footer = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button type="button" style={footerBtn()} onClick={p.onClose} disabled={busy}>
        {C.cancel}
      </button>
      <button
        type="button"
        style={footerBtn(true)}
        onClick={() => void submit()}
        disabled={busy}
        aria-busy={busy || undefined}
      >
        {busy ? C.saving : creating ? C.create : C.save}
      </button>
    </div>
  );

  return (
    <KpiModal
      open
      kicker={KPI_PACKAGES_COPY.page.kicker}
      title={creating ? C.createTitle : C.editTitle}
      onClose={p.onClose}
      dirty={dirty}
      footer={footer}
      width={600}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div style={fieldStyle}>
          <label htmlFor={ids.name} style={labelStyle}>
            {C.name}
          </label>
          <input
            id={ids.name}
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={triedSubmit && nameMissing ? true : undefined}
            aria-describedby={triedSubmit && nameMissing ? `${ids.name}-err` : undefined}
            style={inputStyle}
          />
          {triedSubmit && nameMissing ? (
            <span id={`${ids.name}-err`} style={{ ...helpText, color: "var(--color-error)" }}>
              {C.nameRequired}
            </span>
          ) : null}
        </div>
        <div style={fieldStyle}>
          <label htmlFor={ids.code} style={labelStyle}>
            {C.code}
          </label>
          <input
            id={ids.code}
            value={shownCode}
            readOnly={!creating}
            maxLength={64}
            onChange={(e) => {
              setCodeTouched(true);
              setCode(e.target.value);
            }}
            aria-describedby={`${ids.code}-hint`}
            style={{
              ...inputStyle,
              ...monoStyle,
              ...(creating ? {} : { background: "var(--color-bg)" }),
            }}
          />
          <span id={`${ids.code}-hint`} style={helpText}>
            {C.codeHint}
          </span>
        </div>
        <div style={fieldStyle}>
          <span id={ids.sector} style={labelStyle}>
            {C.sector}
          </span>
          <SearchSelect
            ariaLabel={C.sector}
            value={sector}
            options={SECTOR_OPTIONS}
            onChange={setSector}
            emptyLabel={C.noSector}
            placeholder={C.noSector}
            showCode={false}
          />
        </div>
        <div style={fieldStyle}>
          <span id={ids.fw} style={labelStyle}>
            {C.framework}
          </span>
          <SearchSelect
            ariaLabel={C.framework}
            value={frameworkId}
            options={fwOptions}
            onChange={setFrameworkId}
            emptyLabel={C.noFramework}
            placeholder={C.noFramework}
            showCode={false}
            disabled={frameworks === null}
          />
          {fwFailed ? <span style={helpText}>{C.frameworksFailed}</span> : null}
        </div>
        <div style={fieldStyle}>
          <label htmlFor={ids.desc} style={labelStyle}>
            {C.description}
          </label>
          <textarea
            id={ids.desc}
            value={description}
            maxLength={2000}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
        {error ? (
          <div role="alert" data-alert-variant="error" style={errorBox}>
            {error}
          </div>
        ) : null}
      </form>
    </KpiModal>
  );
}
